import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Calendar,
  Users,
  CheckCircle2,
  Clock,
  Edit,
  Trash2,
  Download,
  Upload,
  Eye,
  X,
  Building2,
  ChevronDown,
  FileUp,
  ClipboardList,
  Gavel,
  AlertCircle,
  Save
} from 'lucide-react';
import Pagination from '../components/Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/*
 * Altam™ Operating Services
 * Meeting Minutes & Governance Documents
 * Copyright (c) 2026 Pecaro Consulting Inc. All rights reserved.
 */

export default function MeetingMinutes() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('minutes');
  const [minutes, setMinutes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  
  // Filters
  const [meetingTypeFilter, setMeetingTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedMinutes, setSelectedMinutes] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    meeting_type: 'board_meeting',
    meeting_date: new Date().toISOString().split('T')[0],
    title: '',
    location: '',
    attendees: '',
    absentees: '',
    content: '',
    next_meeting_date: ''
  });
  
  // Upload form
  const [uploadForm, setUploadForm] = useState({
    document_type: 'minutes',
    title: '',
    meeting_date: '',
    description: ''
  });
  const [uploadFile, setUploadFile] = useState(null);
  
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchProperties();
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (selectedProperty || user?.role === 'board') {
      if (activeTab === 'minutes') {
        fetchMinutes();
      } else {
        fetchDocuments();
      }
    }
  }, [selectedProperty, activeTab, currentPage, meetingTypeFilter, statusFilter, yearFilter]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/corporations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
      if (response.data.length > 0 && user?.role !== 'board') {
        setSelectedProperty(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/governance/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchMinutes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: 10 });
      if (selectedProperty) params.append('entity_id', selectedProperty);
      if (meetingTypeFilter) params.append('meeting_type', meetingTypeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (yearFilter) params.append('year', yearFilter);

      const response = await axios.get(`${API}/governance/minutes?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMinutes(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch minutes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: 10 });
      if (selectedProperty) params.append('entity_id', selectedProperty);

      const response = await axios.get(`${API}/governance/documents?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      const content = template.sections.map(s => `## ${s.title}\n\n${s.content}`).join('\n\n---\n\n');
      setForm(prev => ({
        ...prev,
        meeting_type: templateId,
        title: `${template.name} - ${new Date().toLocaleDateString('en-CA', { month: 'long', year: 'numeric' })}`,
        content
      }));
    }
  };

  const handleCreateMinutes = async () => {
    if (!form.title || !form.meeting_date || !form.content) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        entity_id: selectedProperty,
        meeting_type: form.meeting_type,
        meeting_date: form.meeting_date,
        title: form.title,
        location: form.location,
        attendees: form.attendees ? form.attendees.split(',').map(s => s.trim()) : [],
        absentees: form.absentees ? form.absentees.split(',').map(s => s.trim()) : [],
        content: form.content,
        next_meeting_date: form.next_meeting_date || null,
        status: 'draft'
      };

      await axios.post(`${API}/governance/minutes`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Meeting minutes created successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchMinutes();
    } catch (error) {
      console.error('Failed to create minutes:', error);
      alert('Failed to create minutes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMinutes = async () => {
    if (!selectedMinutes) return;

    setSaving(true);
    try {
      const payload = {
        title: form.title,
        meeting_date: form.meeting_date,
        location: form.location,
        attendees: form.attendees ? form.attendees.split(',').map(s => s.trim()) : [],
        absentees: form.absentees ? form.absentees.split(',').map(s => s.trim()) : [],
        content: form.content,
        next_meeting_date: form.next_meeting_date || null
      };

      await axios.put(`${API}/governance/minutes/${selectedMinutes.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Meeting minutes updated successfully!');
      setShowViewModal(false);
      setEditMode(false);
      fetchMinutes();
    } catch (error) {
      console.error('Failed to update minutes:', error);
      alert('Failed to update minutes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveMinutes = async (minutesId) => {
    if (!confirm('Are you sure you want to approve these meeting minutes?')) return;

    try {
      await axios.post(`${API}/governance/minutes/${minutesId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Meeting minutes approved!');
      fetchMinutes();
      if (showViewModal) {
        setSelectedMinutes(prev => ({ ...prev, status: 'approved' }));
      }
    } catch (error) {
      console.error('Failed to approve minutes:', error);
      alert('Failed to approve minutes.');
    }
  };

  const handleDeleteMinutes = async (minutesId) => {
    if (!confirm('Are you sure you want to delete these meeting minutes? This action cannot be undone.')) return;

    try {
      await axios.delete(`${API}/governance/minutes/${minutesId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Meeting minutes deleted!');
      setShowViewModal(false);
      fetchMinutes();
    } catch (error) {
      console.error('Failed to delete minutes:', error);
      alert('Failed to delete minutes.');
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !uploadForm.title) {
      alert('Please select a file and enter a title');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('entity_id', selectedProperty);
      formData.append('document_type', uploadForm.document_type);
      formData.append('title', uploadForm.title);
      if (uploadForm.meeting_date) formData.append('meeting_date', uploadForm.meeting_date);
      if (uploadForm.description) formData.append('description', uploadForm.description);

      await axios.post(`${API}/governance/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('Document uploaded successfully!');
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadForm({ document_type: 'minutes', title: '', meeting_date: '', description: '' });
      fetchDocuments();
    } catch (error) {
      console.error('Failed to upload document:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await axios.get(`${API}/governance/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      await axios.delete(`${API}/governance/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Document deleted!');
      fetchDocuments();
    } catch (error) {
      console.error('Failed to delete document:', error);
      alert('Failed to delete document.');
    }
  };

  const openViewModal = (item) => {
    setSelectedMinutes(item);
    setForm({
      meeting_type: item.meeting_type,
      meeting_date: item.meeting_date,
      title: item.title,
      location: item.location || '',
      attendees: item.attendees?.join(', ') || '',
      absentees: item.absentees?.join(', ') || '',
      content: item.content,
      next_meeting_date: item.next_meeting_date || ''
    });
    setEditMode(false);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setForm({
      meeting_type: 'board_meeting',
      meeting_date: new Date().toISOString().split('T')[0],
      title: '',
      location: '',
      attendees: '',
      absentees: '',
      content: '',
      next_meeting_date: ''
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getMeetingTypeIcon = (type) => {
    switch (type) {
      case 'agm': return <Users className="w-4 h-4" />;
      case 'board_meeting': return <Gavel className="w-4 h-4" />;
      case 'special_meeting': return <AlertCircle className="w-4 h-4" />;
      case 'committee': return <ClipboardList className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-amber-100 text-amber-700',
      approved: 'bg-emerald-100 text-emerald-700',
      archived: 'bg-slate-100 text-slate-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  const currentYears = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="space-y-6" data-testid="meeting-minutes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Governance Documents</h1>
          <p className="text-slate-500">Meeting minutes, bylaws, and corporate records</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Property Filter */}
          {user?.role !== 'board' && (
            <select
              value={selectedProperty}
              onChange={(e) => {
                setSelectedProperty(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              {properties.map((prop) => (
                <option key={prop.id} value={prop.id}>{prop.name}</option>
              ))}
            </select>
          )}
          
          {/* Action Buttons */}
          {isAdmin && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Minutes
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-6">
          <button
            onClick={() => { setActiveTab('minutes'); setCurrentPage(1); }}
            className={`pb-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'minutes'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Meeting Minutes
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('documents'); setCurrentPage(1); }}
            className={`pb-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'documents'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileUp className="w-4 h-4" />
              Uploaded Documents
            </div>
          </button>
        </nav>
      </div>

      {/* Meeting Minutes Tab */}
      {activeTab === 'minutes' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={meetingTypeFilter}
              onChange={(e) => { setMeetingTypeFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            >
              <option value="">All Types</option>
              <option value="agm">AGM</option>
              <option value="board_meeting">Board Meeting</option>
              <option value="special_meeting">Special Meeting</option>
              <option value="committee">Committee</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="approved">Approved</option>
              <option value="archived">Archived</option>
            </select>
            <select
              value={yearFilter}
              onChange={(e) => { setYearFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            >
              <option value="">All Years</option>
              {currentYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {/* Minutes List */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : minutes.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                <FileText className="w-12 h-12 text-slate-300 mb-3" />
                <p>No meeting minutes found</p>
                {isAdmin && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="mt-3 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                  >
                    Create your first meeting minutes →
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {minutes.map((item) => (
                  <div
                    key={item.id}
                    className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => openViewModal(item)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          item.meeting_type === 'agm' ? 'bg-violet-100 text-violet-600' :
                          item.meeting_type === 'board_meeting' ? 'bg-indigo-100 text-indigo-600' :
                          item.meeting_type === 'special_meeting' ? 'bg-rose-100 text-rose-600' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {getMeetingTypeIcon(item.meeting_type)}
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-900">{item.title}</h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(item.meeting_date)}
                            </span>
                            <span>{item.meeting_type_name}</span>
                            {item.entity_name && user?.role === 'board' && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {item.entity_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(item.status)}`}>
                          {item.status}
                        </span>
                        {isAdmin && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); openViewModal(item); setEditMode(true); }}
                              className="p-1 text-slate-400 hover:text-indigo-600 rounded"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteMinutes(item.id); }}
                              className="p-1 text-slate-400 hover:text-rose-600 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {pagination.pages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.pages}
              totalItems={pagination.total}
              itemsPerPage={10}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : documents.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                <FileUp className="w-12 h-12 text-slate-300 mb-3" />
                <p>No documents uploaded</p>
                {isAdmin && (
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="mt-3 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                  >
                    Upload your first document →
                  </button>
                )}
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Document</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Size</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-rose-100 p-2 rounded-lg">
                            <FileText className="w-4 h-4 text-rose-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{doc.title}</p>
                            <p className="text-xs text-slate-500">{doc.filename}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-600 capitalize">{doc.document_type}</td>
                      <td className="py-3 px-4 text-slate-500 text-sm">{formatDate(doc.uploaded_at)}</td>
                      <td className="py-3 px-4 text-slate-500 text-sm">{formatFileSize(doc.file_size)}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDownloadDocument(doc)}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteDocument(doc.id)}
                              className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {pagination.pages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.pages}
              totalItems={pagination.total}
              itemsPerPage={10}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )}

      {/* Create Minutes Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Create Meeting Minutes</h2>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Start from Template</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template.id)}
                      className={`p-3 border rounded-lg text-left hover:border-indigo-300 hover:bg-indigo-50 transition-colors ${
                        form.meeting_type === template.id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {getMeetingTypeIcon(template.id)}
                        <span className="font-medium text-sm text-slate-900">{template.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., Board Meeting - January 2026"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Meeting Date *</label>
                  <input
                    type="date"
                    value={form.meeting_date}
                    onChange={(e) => setForm({ ...form, meeting_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., Party Room, 123 Main St"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Next Meeting Date</label>
                  <input
                    type="date"
                    value={form.next_meeting_date}
                    onChange={(e) => setForm({ ...form, next_meeting_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Attendees</label>
                  <input
                    type="text"
                    value={form.attendees}
                    onChange={(e) => setForm({ ...form, attendees: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="John Smith, Jane Doe, Bob Wilson"
                  />
                  <p className="text-xs text-slate-500 mt-1">Comma-separated names</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Absentees</label>
                  <input
                    type="text"
                    value={form.absentees}
                    onChange={(e) => setForm({ ...form, absentees: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="Names of absent members"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Meeting Content *</label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                  rows={15}
                  placeholder="Enter meeting minutes content..."
                />
                <p className="text-xs text-slate-500 mt-1">Use ## for headings, --- for dividers</p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 p-4 flex justify-end gap-3">
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateMinutes}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Save className="w-4 h-4" />}
                Save as Draft
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View/Edit Minutes Modal */}
      {showViewModal && selectedMinutes && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-slate-900">
                  {editMode ? 'Edit Meeting Minutes' : 'View Meeting Minutes'}
                </h2>
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(selectedMinutes.status)}`}>
                  {selectedMinutes.status}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && selectedMinutes.status === 'draft' && !editMode && (
                  <button
                    onClick={() => handleApproveMinutes(selectedMinutes.id)}
                    className="px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm flex items-center gap-1"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Approve
                  </button>
                )}
                {isAdmin && !editMode && (
                  <button
                    onClick={() => setEditMode(true)}
                    className="px-3 py-1 border border-slate-300 rounded-lg text-sm flex items-center gap-1 hover:bg-slate-50"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                )}
                <button onClick={() => { setShowViewModal(false); setEditMode(false); }} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {editMode ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                      <input
                        type="text"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Meeting Date</label>
                      <input
                        type="date"
                        value={form.meeting_date}
                        onChange={(e) => setForm({ ...form, meeting_date: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                      <input
                        type="text"
                        value={form.location}
                        onChange={(e) => setForm({ ...form, location: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Next Meeting</label>
                      <input
                        type="date"
                        value={form.next_meeting_date}
                        onChange={(e) => setForm({ ...form, next_meeting_date: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Content</label>
                    <textarea
                      value={form.content}
                      onChange={(e) => setForm({ ...form, content: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-sm"
                      rows={15}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 rounded-lg p-4">
                    <div>
                      <p className="text-xs text-slate-500">Meeting Type</p>
                      <p className="font-medium text-slate-900">{selectedMinutes.meeting_type_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Date</p>
                      <p className="font-medium text-slate-900">{formatDate(selectedMinutes.meeting_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Location</p>
                      <p className="font-medium text-slate-900">{selectedMinutes.location || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Next Meeting</p>
                      <p className="font-medium text-slate-900">{formatDate(selectedMinutes.next_meeting_date)}</p>
                    </div>
                  </div>
                  
                  {selectedMinutes.attendees?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-1">Attendees</p>
                      <p className="text-slate-600">{selectedMinutes.attendees.join(', ')}</p>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Meeting Minutes</p>
                    <div className="bg-slate-50 rounded-lg p-4 whitespace-pre-wrap font-mono text-sm text-slate-700">
                      {selectedMinutes.content}
                    </div>
                  </div>
                </>
              )}
            </div>

            {editMode && (
              <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 p-4 flex justify-end gap-3">
                <button
                  onClick={() => setEditMode(false)}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateMinutes}
                  disabled={saving}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-900">Upload Document</h2>
              <button onClick={() => { setShowUploadModal(false); setUploadFile(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Document Type</label>
                <select
                  value={uploadForm.document_type}
                  onChange={(e) => setUploadForm({ ...uploadForm, document_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  <option value="minutes">Meeting Minutes</option>
                  <option value="bylaw">Bylaw</option>
                  <option value="policy">Policy</option>
                  <option value="agm">AGM Document</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="e.g., AGM Minutes 2025"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Meeting/Document Date</label>
                <input
                  type="date"
                  value={uploadForm.meeting_date}
                  onChange={(e) => setUploadForm({ ...uploadForm, meeting_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  rows={2}
                  placeholder="Optional description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">File *</label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-indigo-400 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    {uploadFile ? (
                      <p className="text-sm text-slate-900 font-medium">{uploadFile.name}</p>
                    ) : (
                      <>
                        <p className="text-sm text-slate-600">Click to select file</p>
                        <p className="text-xs text-slate-400">PDF, DOC, DOCX</p>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowUploadModal(false); setUploadFile(null); }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadDocument}
                disabled={saving || !uploadFile}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Upload className="w-4 h-4" />}
                Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
